import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, CalendarCheck, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterDialog from '../components/RegisterDialog';
import { useSiteContent } from '../context/SiteContentContext';
import { corporateSeries } from '../siteContent';
import { isAuthenticated } from '../utils/auth';

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const CorporateLifeSeries = () => {
  const { corporatelifeseries: c } = useSiteContent();
  const auth = isAuthenticated();

  return (
    <div className="bg-[#FDFBF9]" data-testid="corporate-life-series-page">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAE5E0] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#003366] mb-6">
            <CalendarCheck className="w-4 h-4 text-[#E74C3C]" /> {c.hero.badge}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A]">
            {c.hero.titlePre}<span className="text-[#E74C3C]">{c.hero.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">{c.hero.subtitle}</p>
          <div className="mt-8 flex justify-center">
            {auth ? (
              <RegisterDialog
                kind="series"
                refId="cls-general"
                title="The Corporate Life Series"
                label={c.hero.cta}
                testid="cls-hero-register"
                triggerClassName="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 text-base group"
              />
            ) : (
              <Link to="/membership">
                <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 text-base group" data-testid="cls-join-btn">
                  Join Free to Register <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Timeline — members only */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6">
          {auth ? (
            <div className="space-y-4">
              {(c.series || []).map((s, i) => (
                <div key={s.month} className="flex items-center gap-5 bg-white rounded-3xl border border-[#EAE5E0] p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid={`series-${s.month.toLowerCase()}`}>
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#003366] text-white flex-shrink-0">
                    <Icon name={corporateSeries[i]?.icon || 'CalendarCheck'} className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#E74C3C]">{s.month}</span>
                    <h3 className="font-heading text-lg sm:text-xl font-semibold text-[#1A1A1A] leading-snug">{s.topic}</h3>
                  </div>
                  <RegisterDialog
                    kind="series"
                    refId={`cls-${s.month.toLowerCase()}`}
                    title={s.topic}
                    label="Register"
                    variant="outline"
                    testid={`cls-register-${s.month.toLowerCase()}`}
                    triggerClassName="rounded-full border border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white text-sm px-5"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[2rem] bg-[#003366] text-white p-10 lg:p-14 text-center" data-testid="cls-gate">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-[#E74C3C]" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold">Members Only Series</h3>
              <p className="text-white/80 mt-4 max-w-md mx-auto leading-relaxed">
                The full Corporate Life Series programme — monthly sessions on careers, legal rights, mental wellness and more — is exclusively for Equaltoo members.
              </p>
              <Link to="/membership">
                <Button className="mt-8 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-10 py-6 text-base group" data-testid="cls-gate-join-btn">
                  Join Free to Access <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/login" className="block mt-4 text-sm text-white/60 hover:text-white transition-colors">Already a member? Log in</Link>
            </div>
          )}
        </div>
      </section>

      {/* Closing */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-[2.5rem] bg-[#003366] text-white text-center px-8 py-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">{c.closing.heading}</h2>
            <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto">{c.closing.subtext}</p>
            <Link to="/membership">
              <Button className="mt-8 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-10 py-6 text-base group">
                {c.closing.cta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateLifeSeries;
